// Money on screen, rounded to the penny (D3, Mohammed's decision 2026-09-27).
//
// Every screen used to carry its own copy of one formatter that split the figure on '.' and kept
// the first two characters of the pence - truncation, not rounding. The backend's sums are
// floating-point doubles, so a total of 71387179.24 arrives as 71387179.23999998 and read
// £71,387,179.23, a penny low. This is the one place money is written now.
//
// Rounding is half away from zero, done on the decimal digits of the figure as written rather than
// in binary arithmetic, so 1.005 gives 1.01 (Math.round(1.005 * 100) gives 1.00) and a sum that is
// a whisker under a penny boundary lands on it.
//
// The output is exactly what the old code wrote for a figure: '£', then any minus sign, the pounds
// with thousands separators, and always two places ('£-1,234.50', '£0.00'). A value that is not a
// figure goes through the old code unchanged, so nothing that displayed before displays
// differently now apart from the pence.

export function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

// A plain figure, or one already carrying correctly placed thousands separators.
const PLAIN = /^(-?)(\d+)(?:\.(\d+))?$/
const GROUPED = /^(-?)(\d{1,3}(?:,\d{3})+)(?:\.(\d+))?$/

// The figure's decimal text, or null when it is not a figure at all.
function figureText(value) {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null
    const text = String(value)
    // Exponent form: very small (1e-7) or very large. toFixed writes the small ones out in full;
    // from 1e21 up it keeps the exponent and the value is treated as not a figure.
    return /e/i.test(text) ? value.toFixed(20) : text
  }
  return typeof value === 'string' ? value : null
}

// Adds one to a string of decimal digits: '0999' -> '1000', '99' -> '100'.
function addOne(digits) {
  const out = digits.split('')
  for (let i = out.length - 1; i >= 0; i--) {
    if (out[i] === '9') {
      out[i] = '0'
    } else {
      out[i] = String(Number(out[i]) + 1)
      return out.join('')
    }
  }
  return `1${out.join('')}`
}

// { sign, pounds, pence } of the figure rounded to two places, or null for a non-figure.
// `pounds` has no separators; `sign` is '-' or '', and never '-' on an amount that rounds to 0.
export function roundedParts(value) {
  const text = figureText(value)
  if (text === null) return null
  const match = PLAIN.exec(text) || GROUPED.exec(text)
  if (!match) return null

  let sign = match[1]
  const whole = match[2].replace(/,/g, '')
  const fraction = match[3] || ''

  let pounds
  let pence
  if (fraction.length <= 2) {
    pounds = whole
    pence = fraction.padEnd(2, '0')
  } else {
    // Whole pounds and pence as one string of pennies, then round on the third decimal.
    let pennies = whole + fraction.slice(0, 2)
    if (fraction.charCodeAt(2) >= 53 /* '5' */) pennies = addOne(pennies)
    pounds = pennies.slice(0, -2)
    pence = pennies.slice(-2)
  }

  if (/^0*$/.test(pounds + pence)) sign = ''
  return { sign, pounds, pence }
}

// '1,234.57' - the rounded figure with separators and two places, no pound sign; null for a
// value that is not a figure.
export function formatAmount(value) {
  const parts = roundedParts(value)
  if (!parts) return null
  return `${parts.sign}${numberWithCommas(parts.pounds)}.${parts.pence}`
}

// The pre-D3 formatter, kept only for values that are not figures ('abc' -> '£abc.00').
function legacyCurrency(b, emptyAsBlank) {
  const c = b.toString().split('.')
  if (c[0] && c[0].split(',').length < 2) c[0] = numberWithCommas(c[0])
  if (c[1]) {
    c[1] = c[1].length === 1 ? `${c[1]}0` : c[1].substring(0, 2)
    return `£${c[0]}.${c[1]}`
  }
  if (!c[0] && emptyAsBlank) return ''
  return `£${c[0]}.00`
}

// '£1,234.57'. A leading '£' on the value is dropped first, so it is never doubled. null and
// undefined come back as they are. `emptyAsBlank` writes an empty value as '' instead of '£.00'
// (the grouped report grid always did).
export function formatCurrency(value, { emptyAsBlank = false } = {}) {
  if (value === null || value === undefined) return value
  let b = value
  if (typeof b === 'string' && b[0] === '£') b = b.substr(1)

  const amount = formatAmount(b)
  if (amount !== null) return `£${amount}`
  return legacyCurrency(b, emptyAsBlank)
}
